import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChevronRight, CheckCircle, Cancel, Add } from '@mui/icons-material';
import MealItemCustomizationOptionForm from './MealItemCustomizationOptionForm';
import { formatCurrency } from '../../../../app/utils/formatters';
import { useAppDispatch } from '../../../../app/store/configureStore';
import MealItemCustomizationRuleForm from './MealItemCustomizationRuleForm';
import ListTitleBox from '../../../../app/components/list/ListTitleBox';
import { useParams, useNavigate } from 'react-router-dom';
import EmptyScreen from '../../../../app/components/EmptyScreen';
import LoadingComponent from '../../../../app/layout/LoadingComponent';
import { fetchMealItemCustomizationAsync } from './mealItemCustomizationSlice';
import {
  MealItemCustomizationModel,
  MealItemCustomizationOptionModel,
} from '../../../../app/data/models/mealItems/mealItemCustomizationModel';
import { getLocalizedValue } from '../../../../app/data/models/common/localizedString';
import {
  MealItemCustomizationOptionFormData,
  toMealItemCustomizationOptionFormData,
} from '../../../../app/data/models/mealItems/mealItemCustomizationFormData';

const MealItemCustomizationDetails = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [editingOption, setEditingOption] = useState<
    MealItemCustomizationOptionFormData | undefined
  >(undefined);
  const [isCreatingOption, setIsCreatingOption] = useState(false);
  const [isEditingRules, setIsEditingRules] = useState(false);
  const { restaurantId, customizationId } = useParams();
  const [customization, setCustomization] = useState<
    MealItemCustomizationModel | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMealItemCustomization = async () => {
    setIsLoading(true);
    try {
      const mealItemCustomization = await dispatch(
        fetchMealItemCustomizationAsync({ restaurantId, customizationId })
      );
      setCustomization(
        mealItemCustomization.payload as MealItemCustomizationModel
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMealItemCustomization();
  }, [restaurantId, customizationId]);

  const handleOptionClick = (option: MealItemCustomizationOptionModel) => {
    setEditingOption(toMealItemCustomizationOptionFormData(option));
  };

  const handleBack = () => {
    navigate(`/manager/restaurants/${restaurantId}/meal-item-customizations`);
  };

  const handleCloseOptionForm = (success: boolean) => {
    setEditingOption(undefined);
    setIsCreatingOption(false);
    if (success) {
      fetchMealItemCustomization();
    }
  };

  const handleCloseRuleForm = (success: boolean) => {
    setIsEditingRules(false);
    if (success) {
      fetchMealItemCustomization();
    }
  };

  const header = (
    <ListTitleBox
      title={t('meal-item-customization.details.title')}
      onBackClick={handleBack}
    />
  );

  if (isLoading) return <LoadingComponent header={header} />;

  if (!customization)
    return (
      <EmptyScreen
        title={t('meal-item-customization.details.title')}
        emptyText={t('meal-item-customization.details.empty')}
        onBackClick={handleBack}
      />
    );

  if (editingOption || isCreatingOption) {
    return (
      <MealItemCustomizationOptionForm
        customizationId={customizationId}
        option={editingOption}
        restaurantId={restaurantId}
        onClose={handleCloseOptionForm}
      />
    );
  }

  if (isEditingRules) {
    return (
      <MealItemCustomizationRuleForm
        restaurantId={restaurantId}
        customizationId={customizationId}
        rules={customization.rules}
        onClose={handleCloseRuleForm}
      />
    );
  }

  return (
    <>
      {header}
      <Box
        component={Paper}
        elevation={2}
        sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 2, maxWidth: '100%', mx: 'auto' }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              mb: 2,
            }}
          >
            {t('meal-item-customization.details.customer-instructions')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              whiteSpace: 'pre-wrap', // Preserves formatting for multi-line text
            }}
          >
            {getLocalizedValue(customization.customerInstruction)}
          </Typography>
        </Box>

        {customization.internalNote && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                mb: 2,
              }}
            >
              {t('meal-item-customization.details.notes')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                whiteSpace: 'pre-wrap',
              }}
            >
              {getLocalizedValue(customization.internalNote)}
            </Typography>
          </Box>
        )}

        {/* Add Options List Section */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
              }}
            >
              {t('meal-item-customization.details.options.list')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setIsCreatingOption(true)}
            >
              {t('meal-item-customization.details.options.create')}
            </Button>
          </Box>

          {customization.options.length === 0 ? (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 4 }}
            >
              {t('meal-item-customization.details.options.empty')}
            </Typography>
          ) : (
            <List>
              {customization.options.map((option) => (
                <ListItem
                  key={option.id}
                  button
                  onClick={() => handleOptionClick(option)}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={getLocalizedValue(option.name)}
                    secondary={formatCurrency(option.price)}
                  />
                  <ListItemSecondaryAction>
                    <Chip
                      size="small"
                      icon={option.isAvailable ? <CheckCircle /> : <Cancel />}
                      label={t(
                        option.isAvailable
                          ? 'common.available'
                          : 'common.unavailable'
                      )}
                      color={option.isAvailable ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ '& > *:not(:last-child)': { mb: 3 } }}>
          {/* Rules Section */}
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 3 },
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'grey.50',
                transform: 'translateX(4px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
            onClick={() => setIsEditingRules(true)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ color: 'text.primary', fontWeight: 500 }}
                >
                  {t('meal-item-customization.details.rules.title')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t('meal-item-customization.details.rules.description')}
                </Typography>
              </Box>
              <ChevronRight sx={{ color: 'text.secondary' }} />
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default MealItemCustomizationDetails;
